import type { Order } from '@/types/order';
import { formatGstPercentInput, orderGstRateToDecimal } from '@/lib/orderPricing';

export const ORDER_DURATION_MIN = 0.5;
export const ORDER_DURATION_MAX = 24;
export const ORDER_DURATION_STEP = 0.5;
export const ORDER_DURATION_DEFAULT = 1;

export interface OrderBillingValues {
  serviceDuration: number;
  gstRateDecimal: number;
  totalPrice: number;
  totalPriceWithGst: number;
}

export function clampOrderDuration(value: number): number {
  if (!Number.isFinite(value)) return ORDER_DURATION_DEFAULT;
  const stepped = Math.round(value / ORDER_DURATION_STEP) * ORDER_DURATION_STEP;
  return Math.min(ORDER_DURATION_MAX, Math.max(ORDER_DURATION_MIN, stepped));
}

export function formatDurationHoursLabel(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

export function resolveOrderGstRateDecimal(
  order: Order | null,
  teamGstDecimal: number | null,
): number {
  if (order) {
    const fromOrder = orderGstRateToDecimal(order.gstRate);
    if (fromOrder !== null) return fromOrder;
  }
  if (teamGstDecimal !== null && Number.isFinite(teamGstDecimal) && teamGstDecimal >= 0) {
    return teamGstDecimal;
  }
  return 0.1;
}

export function resolveOrderDurationHours(order: Order | null): number {
  if (order && typeof order.serviceDuration === 'number' && order.serviceDuration > 0) {
    return clampOrderDuration(order.serviceDuration);
  }
  return ORDER_DURATION_DEFAULT;
}

export function initialGstPercentInput(
  order: Order | null,
  teamGstDecimal: number | null,
): string {
  return formatGstPercentInput(resolveOrderGstRateDecimal(order, teamGstDecimal));
}

/** `invoice_info.tax_rate` 是百分比（10 表示 10%）。 */
export function invoiceTaxRateToDecimal(taxRatePercent: number | null | undefined): number | null {
  if (typeof taxRatePercent !== 'number' || !Number.isFinite(taxRatePercent)) return null;
  if (taxRatePercent < 0 || taxRatePercent > 100) return null;
  return Math.round((taxRatePercent / 100) * 10000) / 10000;
}
